import { useState, useEffect, useCallback, useMemo, Fragment } from 'react'
import { apiFetch } from '../services/api'
import { useAuth } from '../context/AuthContext'

const TIPOS = {
  cliente:   { label: 'Cliente',   color: '#3b82f6', bg: '#dbeafe' },
  proveedor: { label: 'Proveedor', color: '#10b981', bg: '#d1fae5' },
}

const FORM_VACIO = {
  razon_social:    '',
  nombre_fantasia: '',
  rut:             '',
  tipo:            'cliente',
  email:           '',
  telefono:        '',
}

// ── Validaciones ───────────────────────────────────────────────────────────────

const validarRut = (rut) => {
  if (!rut) return true
  const limpio = rut.replace(/[^0-9kK]/g, '')
  if (limpio.length < 2) return false
  const cuerpo = limpio.slice(0, -1)
  const dv     = limpio.slice(-1).toUpperCase()
  let suma = 0, multiplo = 2
  for (let i = cuerpo.length - 1; i >= 0; i--) {
    suma += parseInt(cuerpo[i]) * multiplo
    multiplo = multiplo === 7 ? 2 : multiplo + 1
  }
  const dvEsperado = 11 - (suma % 11)
  const dvCalc = dvEsperado === 11 ? '0' : dvEsperado === 10 ? 'K' : String(dvEsperado)
  return dv === dvCalc
}

const formatearRut = (rut) => {
  const limpio = rut.replace(/[^0-9kK]/g, '')
  if (limpio.length < 2) return rut
  const cuerpo = limpio.slice(0, -1)
  const dv     = limpio.slice(-1).toUpperCase()
  return cuerpo.replace(/\B(?=(\d{3})+(?!\d))/g, '.') + '-' + dv
}

const validarTelefono = (tel) => {
  if (!tel) return true
  return /^(\+56)?[\s-]?9[\s-]?\d{4}[\s-]?\d{4}$/.test(tel.replace(/\s/g, ''))
}

const formatearFecha = (f) =>
  new Date(f).toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' })

// ── ModalForm ──────────────────────────────────────────────────────────────────

function ModalForm({ titulo, form, setForm, error, guardando, onSubmit, onClose }) {
  const handleChange = (campo) => (e) =>
    setForm(prev => ({ ...prev, [campo]: e.target.value }))

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">{titulo}</h2>
        </div>

        <form onSubmit={onSubmit}>

          <div className="form-group">
            <label className="form-label">Razón Social *</label>
            <input
              type="text"
              className="form-input"
              required
              autoFocus
              value={form.razon_social}
              onChange={handleChange('razon_social')}
            />
          </div>

          <div className="form-group">
            <label className="form-label">
              Nombre Fantasía <span style={{ color: 'var(--text-light, #64748b)', fontWeight: 400 }}>(opcional)</span>
            </label>
            <input
              type="text"
              className="form-input"
              placeholder="Nombre comercial"
              value={form.nombre_fantasia}
              onChange={handleChange('nombre_fantasia')}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div className="form-group">
              <label className="form-label">
                RUT <span style={{ color: 'var(--text-light, #64748b)', fontWeight: 400 }}>(opcional)</span>
              </label>
              <input
                type="text"
                className="form-input"
                placeholder="12.345.678-9"
                value={form.rut}
                onChange={handleChange('rut')}
                onBlur={e => {
                  if (e.target.value)
                    setForm(prev => ({ ...prev, rut: formatearRut(e.target.value) }))
                }}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Tipo *</label>
              <select
                className="form-input"
                value={form.tipo}
                onChange={handleChange('tipo')}
              >
                <option value="cliente">Cliente</option>
                <option value="proveedor">Proveedor</option>
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div className="form-group">
              <label className="form-label">
                Email <span style={{ color: 'var(--text-light, #64748b)', fontWeight: 400 }}>(opcional)</span>
              </label>
              <input
                type="email"
                className="form-input"
                placeholder="contacto@empresa.cl"
                value={form.email}
                onChange={handleChange('email')}
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                Teléfono <span style={{ color: 'var(--text-light, #64748b)', fontWeight: 400 }}>(opcional)</span>
              </label>
              <input
                type="text"
                className="form-input"
                placeholder="+56 9 1234 5678"
                value={form.telefono}
                onChange={handleChange('telefono')}
              />
            </div>
          </div>

          {error && (
            <div style={{
              color: 'var(--danger, #ef4444)',
              fontSize: '0.875rem',
              padding: '0.75rem',
              background: '#fef2f2',
              borderRadius: '0.375rem',
              marginBottom: '1rem',
            }}>
              {error}
            </div>
          )}

          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={guardando}>
              {guardando ? '⏳ Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Componente principal ───────────────────────────────────────────────────────

export default function Contactos() {
  const { perfil } = useAuth()
  const rol = perfil?.rol || 'admin'

  const [contactos,   setContactos]   = useState([])
  const [loading,     setLoading]     = useState(true)
  const [busqueda,    setBusqueda]    = useState('')
  const [filtroTipo,  setFiltroTipo]  = useState('')
  const [expandido,   setExpandido]   = useState(null)
  const [modalNuevo,  setModalNuevo]  = useState(false)
  const [modalEditar, setModalEditar] = useState(null)
  const [guardando,   setGuardando]   = useState(false)
  const [error,       setError]       = useState('')
  const [form,        setForm]        = useState(FORM_VACIO)

  const cargarContactos = useCallback(async () => {
    setLoading(true)
    try {
      const data = await apiFetch('/contactos')
      if (Array.isArray(data)) {
        setContactos(data)
      } else {
        setContactos([])
      }
    } catch (err) {
      console.warn('No se pudieron obtener los contactos del servidor:', err)
      setContactos([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    cargarContactos()
  }, [cargarContactos])

  const handleGuardar = async (e) => {
    e.preventDefault()
    setError('')

    if (form.rut && !validarRut(form.rut)) {
      setError('RUT inválido. Verifica el dígito verificador.')
      return
    }
    if (form.telefono && !validarTelefono(form.telefono)) {
      setError('Teléfono inválido. Formato esperado: +56 9 XXXX XXXX')
      return
    }

    setGuardando(true)
    try {
      const payload = {
        razon_social:    form.razon_social.trim(),
        nombre_fantasia: form.nombre_fantasia.trim() || null,
        rut:             form.rut.trim() ? formatearRut(form.rut.trim()) : null,
        tipo:            form.tipo,
        email:           form.email.trim()           || null,
        telefono:        form.telefono.trim()         || null,
        activo:          true,
        created_at:      new Date().toISOString()
      }

      if (modalEditar) {
        try {
          await apiFetch(`/contactos/${modalEditar.id}`, {
            method: 'PATCH',
            body: JSON.stringify(payload),
          })
        } catch (err) {
          console.warn('Actualizado localmente:', err)
        }
        setContactos(prev => prev.map(c => c.id === modalEditar.id ? { ...c, ...payload } : c))
        setModalEditar(null)
      } else {
        let nuevoContacto = { id: `c-${Date.now()}`, ...payload }
        try {
          const res = await apiFetch('/contactos', {
            method: 'POST',
            body: JSON.stringify(payload),
          })
          if (res?.id) nuevoContacto = res
        } catch (err) {
          console.warn('Creado localmente:', err)
        }
        setContactos(prev => [nuevoContacto, ...prev])
        setModalNuevo(false)
      }

      setForm(FORM_VACIO)
    } catch (err) {
      setError(err?.message || 'Error al guardar el contacto')
    } finally {
      setGuardando(false)
    }
  }

  const handleToggleActivo = async (contacto) => {
    const nuevoEstado = !contacto.activo
    const msg = nuevoEstado
      ? `¿Reactivar "${contacto.razon_social}"?`
      : `¿Desactivar "${contacto.razon_social}"?`
    if (!confirm(msg)) return

    try {
      await apiFetch(`/contactos/${contacto.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ activo: nuevoEstado }),
      })
    } catch (err) {
      console.warn('Estado cambiado localmente:', err)
    }

    setContactos(prev => prev.map(c => c.id === contacto.id ? { ...c, activo: nuevoEstado } : c))
  }

  const abrirEditar = (contacto) => {
    setForm({
      razon_social:    contacto.razon_social,
      nombre_fantasia: contacto.nombre_fantasia || '',
      rut:             contacto.rut             || '',
      tipo:            contacto.tipo === 'ambos' ? 'cliente' : contacto.tipo,
      email:           contacto.email           || '',
      telefono:        contacto.telefono        || '',
    })
    setError('')
    setModalEditar(contacto)
  }

  const cerrarModal = () => {
    setModalNuevo(false)
    setModalEditar(null)
    setForm(FORM_VACIO)
    setError('')
  }

  const contactosFiltrados = useMemo(() => {
    const term = busqueda.toLowerCase()
    return contactos.filter(c => {
      const coincideTexto =
        c.razon_social.toLowerCase().includes(term) ||
        (c.nombre_fantasia || '').toLowerCase().includes(term) ||
        (c.rut             || '').toLowerCase().includes(term)
      const coincideTipo = filtroTipo ? c.tipo === filtroTipo : true
      return coincideTexto && coincideTipo
    })
  }, [contactos, busqueda, filtroTipo])

  const resumen = useMemo(() => ({
    cliente:   contactos.filter(c => c.tipo === 'cliente').length,
    proveedor: contactos.filter(c => c.tipo === 'proveedor').length,
  }), [contactos])

  if (loading) return (
    <div className="main-content">
      <div className="page-header"><h1 className="page-title">Contactos</h1></div>
      <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>Cargando contactos...</div>
    </div>
  )

  return (
    <div className="main-content">
      <div className="page-header">
        <div className="flex-between">
          <div>
            <h1 className="page-title">Contactos</h1>
            <p className="page-subtitle">{contactos.length} contactos registrados</p>
          </div>
          {rol === 'admin' && (
            <button
              className="btn btn-primary"
              onClick={() => { setForm(FORM_VACIO); setError(''); setModalNuevo(true) }}
            >
              + Nuevo Contacto
            </button>
          )}
        </div>
      </div>

      <div className="stats-grid" style={{ marginBottom: '1.5rem', display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
        {Object.entries(TIPOS).map(([tipo, meta]) => (
          <div
            key={tipo}
            className="stat-card"
            style={{
              cursor: 'pointer',
              padding: '1.25rem',
              background: '#fff',
              borderRadius: '10px',
              border: '1px solid #e2e8f0',
              borderLeft: `4px solid ${meta.color}`,
              opacity: filtroTipo && filtroTipo !== tipo ? 0.5 : 1,
            }}
            onClick={() => setFiltroTipo(f => f === tipo ? '' : tipo)}
          >
            <div className="stat-label" style={{ color: '#64748b', fontSize: '0.875rem' }}>{meta.label}s</div>
            <div className="stat-value" style={{ color: meta.color, fontSize: '1.5rem', fontWeight: 'bold' }}>{resumen[tipo] || 0}</div>
          </div>
        ))}
      </div>

      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <input
          type="text"
          className="search-input"
          placeholder="Buscar por razón social, nombre fantasía o RUT..."
          value={busqueda}
          onChange={e => setBusqueda(e.target.value)}
        />
      </div>

      <div className="card">
        <div style={{ marginBottom: '1rem', fontSize: '0.875rem', color: 'var(--text-light, #64748b)' }}>
          {contactosFiltrados.length} resultado{contactosFiltrados.length !== 1 ? 's' : ''}
        </div>

        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Razón Social</th>
                <th>RUT</th>
                <th>Tipo</th>
                <th>Contacto</th>
                <th>Estado</th>
                {rol === 'admin' && <th>Acciones</th>}
              </tr>
            </thead>
            <tbody>
              {contactosFiltrados.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-light, #64748b)' }}>
                    {busqueda || filtroTipo ? 'No se encontraron contactos' : 'No hay contactos registrados'}
                  </td>
                </tr>
              ) : contactosFiltrados.map(c => (
                <Fragment key={c.id}>
                  <tr style={{ opacity: c.activo ? 1 : 0.5 }}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <div>
                          <div style={{ fontWeight: '500' }}>{c.razon_social}</div>
                          {c.nombre_fantasia && (
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-light, #64748b)' }}>
                              {c.nombre_fantasia}
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => setExpandido(expandido === c.id ? null : c.id)}
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            fontSize: '0.75rem', color: 'var(--text-light, #64748b)',
                            padding: '0.1rem 0.3rem',
                          }}
                        >
                          {expandido === c.id ? '▲ menos' : '▼ más'}
                        </button>
                      </div>
                    </td>
                    <td style={{ fontSize: '0.875rem' }}>{c.rut || '—'}</td>
                    <td>
                      <span style={{
                        padding: '0.25rem 0.6rem', borderRadius: '999px',
                        fontSize: '0.75rem', fontWeight: '600',
                        color:       TIPOS[c.tipo]?.color  || '#6b7280',
                        background:  TIPOS[c.tipo]?.bg     || '#f1f5f9',
                      }}>
                        {TIPOS[c.tipo]?.label || c.tipo}
                      </span>
                    </td>
                    <td style={{ fontSize: '0.875rem' }}>
                      <div>{c.email    || '—'}</div>
                      <div style={{ color: 'var(--text-light, #64748b)' }}>{c.telefono || ''}</div>
                    </td>
                    <td>
                      {c.activo
                        ? <span className="badge badge-success">Activo</span>
                        : <span className="badge badge-danger">Inactivo</span>}
                    </td>
                    {rol === 'admin' && (
                      <td>
                        <div style={{ display: 'flex', gap: '0.4rem' }}>
                          <button
                            className="btn btn-outline"
                            onClick={() => abrirEditar(c)}
                            style={{ padding: '0.4rem 0.75rem', fontSize: '0.8rem' }}
                          >
                            Editar
                          </button>
                          <button
                            className="btn btn-outline"
                            onClick={() => handleToggleActivo(c)}
                            style={{ padding: '0.4rem 0.75rem', fontSize: '0.8rem' }}
                          >
                            {c.activo ? 'Desactivar' : 'Activar'}
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>

                  {expandido === c.id && (
                    <tr>
                      <td colSpan="6" style={{ padding: 0, background: '#f8fafc' }}>
                        <div style={{
                          padding: '1.25rem 1.5rem',
                          borderTop: '2px solid var(--primary, #2563eb)',
                          borderBottom: '1px solid var(--border, #e2e8f0)',
                        }}>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem' }}>
                            {[
                              ['EMAIL',           c.email],
                              ['TELÉFONO',        c.telefono],
                              ['NOMBRE FANTASÍA', c.nombre_fantasia],
                              ['REGISTRADO',      formatearFecha(c.created_at)],
                            ].map(([label, valor]) => (
                              <div key={label}>
                                <div style={{ fontSize: '0.75rem', color: 'var(--text-light, #64748b)', fontWeight: '600', marginBottom: '0.25rem' }}>
                                  {label}
                                </div>
                                <div style={{ fontSize: '0.875rem' }}>
                                  {valor || <span style={{ color: 'var(--text-light, #64748b)' }}>No registrado</span>}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {modalNuevo && (
        <ModalForm
          titulo="Nuevo Contacto"
          form={form}
          setForm={setForm}
          error={error}
          guardando={guardando}
          onSubmit={handleGuardar}
          onClose={cerrarModal}
        />
      )}
      {modalEditar && (
        <ModalForm
          titulo={`Editar: ${modalEditar.razon_social}`}
          form={form}
          setForm={setForm}
          error={error}
          guardando={guardando}
          onSubmit={handleGuardar}
          onClose={cerrarModal}
        />
      )}
    </div>
  )
}